import { useCallback, useMemo, useState } from "react"

interface VehicleSelectionItem {
	sharingId?: string
}

export const useVehicleSelection = (items: VehicleSelectionItem[]) => {
	const [selectedVehicles, setSelectedVehicles] = useState<Set<string>>(() => new Set<string>())

	const itemsWithId: { sharingId: string }[] = useMemo(
		() =>
			items.reduce(
				(res, { sharingId }) => {
					if (sharingId) {
						res.push({ sharingId })
					}
					return res
				},
				[] as { sharingId: string }[],
			),
		[items],
	)

	const currentIds = useMemo(() => new Set(itemsWithId.map(({ sharingId }) => sharingId)), [itemsWithId])

	const currentVehiclesSelectionState: boolean | "indeterminate" | undefined = useMemo(() => {
		const intersection = selectedVehicles.intersection(currentIds)

		// TODO check vins
		return intersection.size === 0
			? false
			: intersection.size === currentIds.size && intersection.isSubsetOf(currentIds)
				? true
				: "indeterminate"
	}, [currentIds, selectedVehicles])

	const onCurrentVehiclesSelectionToggle = useCallback(
		() =>
			setSelectedVehicles((prev) =>
				currentVehiclesSelectionState === true ? prev.difference(currentIds) : prev.union(currentIds),
			),
		[currentIds, currentVehiclesSelectionState],
	)

	const onVehicleSelectionToggle = useCallback((vin: string) => {
		setSelectedVehicles((prev) => {
			const newVal = new Set(prev)
			if (newVal.has(vin)) {
				newVal.delete(vin)
			} else {
				newVal.add(vin)
			}
			return newVal
		})
	}, [])

	const onAllVehiclesSelectionClear = useCallback(() => {
		setSelectedVehicles(new Set())
	}, [])

	return {
		currentVehiclesSelectionState,
		onAllVehiclesSelectionClear,
		onCurrentVehiclesSelectionToggle,
		onVehicleSelectionToggle,
		selectedVehicles,
	}
}
